import { Tooltip } from "components/general/Tooltip";
import { PropsWithChildren } from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { Token } from "store/global/TokenEnum";
import { getWrappedSymbolForChain } from "utils/chainUtils";
import { TokenRowDisabledReason } from "utils/tokenSelectorUtils";
import {
  AllOtherTokensDisabledHoverContent,
  OpposingPartyNon121TooltipContent,
  SamePartyTooltipContent,
} from "./SelectTokenModalTooltipContents";
import { CounterParty } from "utils/tradeUtils";

interface TooltipProps {
  counterParty: CounterParty;
  symbol: Token;
  tokenDisabledReason: TokenRowDisabledReason | null;
}

export const SelectTokenRowTooltip = ({
  counterParty,
  symbol,
  tokenDisabledReason,
  children,
}: PropsWithChildren<TooltipProps>) => {
  const network = GlobalStore.useState(s => s.network);
  const wrappedSymbolForChain = getWrappedSymbolForChain(network);
  const nativeSymbolForChain = network.nativeCurrency.symbol as Token;
  const isDisabled = tokenDisabledReason != null;

  if (!isDisabled) {
    return <>{children}</>;
  }

  const TooltipContent = () => {
    const isTaker = counterParty === "taker";
    if (tokenDisabledReason === TokenRowDisabledReason.IsWrapTrade) {
      return (
        <AllOtherTokensDisabledHoverContent
          nativeToken={nativeSymbolForChain}
          wrappedToken={wrappedSymbolForChain}
          isTaker={isTaker}
        />
      );
    }

    if (symbol !== wrappedSymbolForChain && symbol !== nativeSymbolForChain) {
      return null;
    }

    const oppositeSymbol =
      symbol === nativeSymbolForChain ? wrappedSymbolForChain : nativeSymbolForChain;

    if (tokenDisabledReason === TokenRowDisabledReason.SelectedTokensAlreadyIncludesNatives) {
      return (
        <SamePartyTooltipContent
          currentSymbol={symbol}
          otherSymbol={oppositeSymbol}
          isTaker={isTaker}
        />
      );
    }

    if (tokenDisabledReason === TokenRowDisabledReason.OppositePartyHasNativesAndIsNon121Trade) {
      return (
        <OpposingPartyNon121TooltipContent
          toPay={isTaker ? symbol : oppositeSymbol}
          toReceive={isTaker ? oppositeSymbol : symbol}
          isTaker={isTaker}
        />
      );
    }

    return null;
  };

  if (TooltipContent() == null) {
    return <>{children}</>;
  }

  return (
    <Tooltip placement="top" title={TooltipContent}>
      {children}
    </Tooltip>
  );
};
